import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import styled from "styled-components";
import {
	AddCircle,
	Close,
	Delete,
	Edit,
	Photo,
	Visibility,
	VisibilityOff,
} from "@material-ui/icons";
import API from "../../network/dashboardAPI";
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardMedia,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Snackbar,
	SnackbarContent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import FileUploadService from "../../services/FileUploadService";

const statuses = [
	{ id: 0, label: "All", value: "" },
	{ id: 1, label: "Active", value: 1 },
	{ id: 2, label: "Inactive", value: 0 },
];

const terminalTypes = [
	{ id: 0, label: "All", value: "" },
	{ id: 1, label: "App", value: "app" },
	{ id: 2, label: "Web", value: "web" },
];

const storeTypes = [
	{ id: 0, label: "All", value: "" },
	{ id: 1, label: "Express", value: "1" },
	{ id: 2, label: "Normal", value: "0" },
];

function StoreLogo() {
	const [storeLogos, setStoreLogos] = useState({});

	// progress
	const [loading, setLoading] = useState(false);
	const [isUploadingLogo, setIsUploadingLogo] = useState(false);

	// form
	const [id, setId] = useState(null);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [logoUrl, setLogoUrl] = useState("");
	const [terminalType, setTerminalType] = useState("");
	const [storeType, setStoreType] = useState("");
	const _logoUrlRef = useRef(null);

	const [filter, setFilter] = useState({
		status: "",
		terminal_type: "",
		store_type: "",
	});

	// form error
	const [formError, setFormError] = useState({
		terminal_type: "",
		store_type: "",
		logo: "",
	});

	// dialog form
	const [openDialog, setOpenDialog] = useState(false);
	const [dialogMode, setDialogMode] = useState(""); // create || edit

	// toast Message
	const [showToast, setShowToast] = useState(false);
	const [toastMessage, setToastMessage] = useState("");
	const [toastBgColor, setToastBgColor] = useState("#9bc44b");

	// pagination
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// image preview
	const [dialogImgOpen, setDialogImgOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState({});

	const payload = useMemo(() => {
		return {
			page: page + 1,
			limit: rowsPerPage,
			status: filter.status,
			terminal_type: filter.terminal_type,
			type: filter.store_type,
		};
	}, [
		page,
		rowsPerPage,
		filter.status,
		filter.terminal_type,
		filter.store_type,
	]);

	const getAdminStoreLogos = useCallback((body) => {
		setLoading(true);
		API.getAdminStoreLogos(body)
			.then((res) => res.json())
			.then((res) => {
				if (res.success) {
					setStoreLogos(res.data);
				} else {
					console.log(res);
				}
				setLoading(false);
			});
	}, []);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setPage(0);
		setRowsPerPage(parseInt(event.target.value));
	};

	const handleFilterChange = (event) => {
		const { value, name } = event.target;
		setFilter((prev) => ({ ...prev, [name]: value }));
	};

	const handleFileChange = async () => {
		try {
			setIsUploadingLogo(true);
			let formData = null;

			if (_logoUrlRef.current !== null) {
				formData = new FormData();
				formData.append("image", _logoUrlRef.current.files[0]);

				await FileUploadService.uploadImageFromDashboard(formData).then(
					(response) => {
						if (response.data.success && response.data.data.length) {
							setFormError({
								...formError,
								logo: "",
							});
							handleShowToast("success", "Uploaded Successfully.");
							setLogoUrl(response.data.data[0].url);
						}
						setIsUploadingLogo(false);
					}
				);
			}
		} catch (e) {
			console.log("Debug: ", e);
			handleShowToast("fail", e);
			setIsUploadingLogo(false);
		}
	};

	const handleChangeTerminalType = (event) => {
		setTerminalType(event.target.value);
		setFormError({
			...formError,
			terminal_type: "",
		});
	};

	const handleChangeStoreType = (event) => {
		setStoreType(parseInt(event.target.value));
		setFormError({
			...formError,
			store_type: "",
		});
	};

	const validateForm = () => {
		let tmpFormError = {
			terminal_type: "",
			store_type: "",
			logo: "",
		};

		if (terminalType === "") tmpFormError.terminal_type = "is required";
		if (storeType === "") tmpFormError.store_type = "is required";
		if (logoUrl === "") tmpFormError.logo = "is required";

		if (Object.values(tmpFormError).every((el) => el === "")) {
			return true;
		} else {
			setFormError({
				terminal_type: tmpFormError.terminal_type,
				store_type: tmpFormError.store_type,
				logo: tmpFormError.logo,
			});
			return false;
		}
	};

	const handleResetFilters = () => {
		setFilter({ status: "", terminal_type: "", store_type: "" });
		setPage(0);
		setRowsPerPage(10);
	};

	const handleResetForm = () => {
		setId(null);
		setTitle("");
		setDescription("");
		setLogoUrl("");
		setTerminalType("");
		setStoreType("");
	};

	const handleResetFormError = () => {
		setFormError({ terminal_type: "", store_type: "", logo: "" });
	};

	const handleShowToast = (type = "", msg = "") => {
		setShowToast(true);
		setToastMessage(msg);
		switch (type) {
			case "success":
				setToastBgColor("#9bc44b");
				break;
			case "fail":
				setToastBgColor("#dc3545");
				break;
			default:
				break;
		}
	};

	const handleToggleDialogImg = (item = {}) => {
		return () => {
			setDialogImgOpen((prev) => !prev);
			setSelectedItem(item);
		};
	};

	const handleOpenCloseDialog = (mode = "", data = {}) => {
		setOpenDialog((prev) => !prev);
		setDialogMode(mode);
		handleResetFormError();
		switch (mode) {
			case "create":
				break;
			case "edit":
				setId(data.id);
				setTitle(data.title);
				setDescription(data.description);
				setLogoUrl(data.logo_url);
				setTerminalType(data.terminal_type);
				setStoreType(data.type);
				break;
			default:
				handleResetForm();
				break;
		}
	};

	const handleSubmitCreateEdit = (e) => {
		e.preventDefault();
		const body = {
			title: title,
			description: description,
			logo_url: logoUrl,
			terminal_type: terminalType,
			type: storeType,
		};
		if (!validateForm()) return;
		setLoading(true);
		switch (dialogMode) {
			case "create":
				API.createAdminStoreLogo(body)
					.then((res) => res.json())
					.then((res) => {
						if (res.success) {
							handleShowToast("success", "Created Successfully.");
							handleOpenCloseDialog();
							handleResetFilters();
							handleResetForm();
							getAdminStoreLogos(payload);
						} else {
							handleShowToast("fail", res.message);
						}
						setLoading(false);
					});
				break;
			case "edit":
				API.updateAdminStoreLogo({ id, ...body })
					.then((res) => res.json())
					.then((res) => {
						if (res.success) {
							handleShowToast("success", "Updated Successfully.");
							handleOpenCloseDialog();
							handleResetForm();
							getAdminStoreLogos(payload);
						} else {
							handleShowToast("fail", res.message);
						}
						setLoading(false);
					});
				break;
			default:
				break;
		}
	};

	const handleEnableDisableLogo = (item) => {
		return () => {
			if (
				!window.confirm(
					`Are you sure you want to ${
						item.is_active ? "disable" : "enable"
					} this logo?`
				)
			) {
				return;
			}

			API.updateAdminStoreLogo({
				id: item.id,
				is_active: item.is_active === 1 ? 0 : 1,
			})
				.then((res) => res.json())
				.then((res) => {
					if (res.success) {
						handleShowToast("success", "Updated Successfully.");
						handleResetForm();
						getAdminStoreLogos(payload);
					} else {
						handleShowToast("fail", res.message);
					}
				});
		};
	};

	const handleDeleteStoreLogo = (id) => {
		return () => {
			if (!window.confirm("Are you sure you want to delete this logo?")) return;
			API.deleteAdminStoreLogo({ id })
				.then((res) => res.json())
				.then((res) => {
					if (res.success) {
						handleShowToast("success", "Deleted Successfully.");
						handleResetForm();
						getAdminStoreLogos(payload);
					} else {
						handleShowToast("fail", res.message);
					}
				});
		};
	};

	useEffect(() => {
		getAdminStoreLogos(payload);
	}, [payload, getAdminStoreLogos]);

	return (
		<HelpPageWrapper>
			<Paper className="p-3">
				<Grid container spacing={1}>
					<Grid item container>
						<Grid item xs={12} md={6}>
							<h4 className="mb-3">Store Logo</h4>
						</Grid>
						<Grid item container xs={12}>
							<Grid item container spacing={1}>
								<Grid item container xs={12} md={6} lg={3}>
									<FormControl variant="filled" size="small" className="w-100">
										<InputLabel>Status</InputLabel>
										<Select
											disabled={loading}
											name="status"
											value={filter.status}
											onChange={handleFilterChange}
										>
											{statuses.map((status) => (
												<MenuItem value={status.value} key={status.id}>
													{status.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item container xs={12} md={6} lg={3}>
									<FormControl variant="filled" size="small" className="w-100">
										<InputLabel>Terminal</InputLabel>
										<Select
											disabled={loading}
											name="terminal_type"
											value={filter.terminal_type}
											onChange={handleFilterChange}
										>
											{terminalTypes.map((terminal) => (
												<MenuItem value={terminal.value} key={terminal.id}>
													{terminal.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item container xs={12} md={6} lg={3}>
									<FormControl variant="filled" size="small" className="w-100">
										<InputLabel>Store Type</InputLabel>
										<Select
											disabled={loading}
											name="store_type"
											value={filter.store_type}
											onChange={handleFilterChange}
										>
											{storeTypes.map((type) => (
												<MenuItem value={type.value} key={type.id}>
													{type.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid
									item
									container
									xs={12}
									md={6}
									lg={3}
									justifyContent="flex-end"
									alignItems="flex-end"
								>
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={() => handleOpenCloseDialog("create")}
										startIcon={<AddCircle />}
									>
										New Logo
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item container spacing={1}>
						<Grid item xs={12}>
							<TableContent>
								<Paper variant="outlined">
									<TableContainer style={{ position: "relative" }}>
										{loading && (
											<div
												style={{
													display: "block",
													position: "absolute",
													inset: 0,
													background: "rgb(166 166 166 / 20%)",
												}}
											/>
										)}
										<Table>
											<TableHead>
												<TableRow>
													<TableCell component="th">Logo</TableCell>
													<TableCell component="th">Title</TableCell>
													<TableCell component="th">Description</TableCell>
													<TableCell component="th" align="center">
														Terminal Type
													</TableCell>
													<TableCell component="th" align="center">
														Store Type
													</TableCell>
													<TableCell component="th" align="center">
														Status
													</TableCell>
													<TableCell component="th" align="center">
														Actions
													</TableCell>
												</TableRow>
											</TableHead>

											<TableBody>
												{storeLogos.list?.length ? (
													storeLogos?.list?.map((item) => {
														return (
															<TableRow key={item?.id}>
																<TableCell>
																	<img
																		alt={item?.title}
																		src={item?.logo_url}
																		style={{
																			height: 50,
																			width: "auto",
																			objectFit: "contain",
																			cursor: "pointer",
																		}}
																		onClick={handleToggleDialogImg(item)}
																	/>
																</TableCell>
																<TableCell>{item.title || "-"}</TableCell>
																<TableCell>{item.description || "-"}</TableCell>
																<TableCell
																	align="center"
																	style={{ textTransform: "capitalize" }}
																>
																	{item.terminal_type || "-"}
																</TableCell>
																<TableCell
																	align="center"
																	style={{ textTransform: "capitalize" }}
																>
																	{item.type === 1 ? "Express" : "Normal"}
																</TableCell>
																<TableCell align="center">
																	<div>
																		{item?.is_active ? (
																			<Tooltip title="Disable">
																				<IconButton
																					color="primary"
																					onClick={handleEnableDisableLogo(item)}
																				>
																					<Visibility />
																				</IconButton>
																			</Tooltip>
																		) : (
																			<Tooltip title="Enable" arrow>
																				<IconButton
																					color="default"
																					onClick={handleEnableDisableLogo(item)}
																				>
																					<VisibilityOff />
																				</IconButton>
																			</Tooltip>
																		)}
																	</div>
																</TableCell>
																<TableCell align="center">
																	<Tooltip title="Edit" arrow>
																		<IconButton
																			color="primary"
																			centerRipple
																			onClick={() => handleOpenCloseDialog("edit", item)}
																		>
																			<Edit />
																		</IconButton>
																	</Tooltip>
																	<Tooltip title="Delete" arrow>
																		<IconButton
																			color="secondary"
																			centerRipple
																			onClick={handleDeleteStoreLogo(item.id)}
																		>
																			<Delete />
																		</IconButton>
																	</Tooltip>
																</TableCell>
															</TableRow>
														);
													})
												) : (
													<TableRow>
														<TableCell colSpan={7} className="text-center">
															No Data
														</TableCell>
													</TableRow>
												)}
											</TableBody>
										</Table>
									</TableContainer>
									<div
										style={{
											display: "block",
											width: "100%",
											height: "5px",
										}}
									>
										{loading && <LinearProgress variant="query" />}
									</div>
									<TablePagination
										component="div"
										count={storeLogos?.total_items || 0}
										page={page}
										rowsPerPage={rowsPerPage}
										rowsPerPageOptions={[5, 10, 25, 50]}
										onPageChange={handleChangePage}
										onRowsPerPageChange={handleChangeRowsPerPage}
									/>
								</Paper>
							</TableContent>
						</Grid>
					</Grid>
				</Grid>
			</Paper>

			{/* Create/Edit From Dialog */}
			<Dialog fullWidth scroll="body" key={dialogMode} open={openDialog}>
				<DialogTitle>Add Logo</DialogTitle>
				<DialogContent dividers>
					<FormWrapper>
						<Grid container spacing={1} style={{ flexGrow: 1 }}>
							<Grid item xs={12} sm={6}>
								<FormControl component="fieldset" error={formError.terminal_type}>
									<FormLabel component="legend">
										* Terminal Type {formError.terminal_type && formError.terminal_type}
									</FormLabel>
									<RadioGroup
										aria-label="terminalType"
										value={terminalType}
										style={{ flexDirection: "row" }}
										onChange={handleChangeTerminalType}
									>
										<FormControlLabel
											value="app"
											control={<Radio color="primary" />}
											label="App"
										/>
										<FormControlLabel
											value="web"
											control={<Radio color="primary" />}
											label="Web"
										/>
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControl component="fieldset" error={formError.store_type}>
									<FormLabel component="legend">
										* Store Type {formError.store_type && formError.store_type}
									</FormLabel>
									<RadioGroup
										aria-label="storeType"
										value={storeType}
										style={{ flexDirection: "row" }}
										onChange={handleChangeStoreType}
									>
										<FormControlLabel
											value={1}
											control={<Radio color="primary" />}
											label="Express"
										/>
										<FormControlLabel
											value={0}
											control={<Radio color="primary" />}
											label="Normal"
										/>
									</RadioGroup>
								</FormControl>
							</Grid>
						</Grid>

						<TextField
							variant="outlined"
							label="Title"
							value={title}
							disabled={loading}
							onChange={(e) => setTitle(e.target.value)}
						/>
						<TextField
							variant="outlined"
							label="Description"
							type="textarea"
							multiline
							minRows={3}
							value={description}
							disabled={loading}
							onChange={(e) => setDescription(e.target.value)}
						/>
						<FormControl error={formError.logo}>
							<FormLabel>* Logo {formError.logo && formError.logo}</FormLabel>
							<TextField
								variant="outlined"
								type="file"
								inputRef={_logoUrlRef}
								disabled={isUploadingLogo || loading}
								error={formError.logo}
								onChange={handleFileChange}
							/>
						</FormControl>
						<Card
							style={{
								height: 230,
								width: "100%",
								display: "grid",
								placeItems: "center",
								padding: 10,
							}}
						>
							{isUploadingLogo ? (
								<CircularProgress color="primary" />
							) : logoUrl ? (
								<img
									alt="logo"
									src={logoUrl}
									style={{ height: 200, width: "100%", objectFit: "contain" }}
								/>
							) : (
								<Photo />
							)}
						</Card>
					</FormWrapper>
				</DialogContent>
				<DialogActions>
					<Button
						variant="contained"
						color="primary"
						size="small"
						disabled={loading || isUploadingLogo}
						onClick={handleSubmitCreateEdit}
					>
						{dialogMode === "create"
							? "Add"
							: dialogMode === "edit"
							? "Update"
							: "Submit"}
					</Button>
					<Button
						variant="outlined"
						color="default"
						size="small"
						onClick={() => handleOpenCloseDialog("")}
					>
						Dismiss
					</Button>
				</DialogActions>
			</Dialog>

			{/* Dialog Image */}
			<Dialog
				open={dialogImgOpen}
				maxWidth="xs"
				fullWidth
				onClose={handleToggleDialogImg({})}
			>
				<DialogContent className="p-0">
					<Card className="p-3">
						<CardMedia
							component="img"
							alt="logo"
							image={selectedItem.logo_url}
							style={{
								width: "100%",
								height: "auto",
								objectFit: "contain",
								padding: 8,
							}}
						/>
						<CardContent>
							<Typography gutterBottom variant="h5" component="h2">
								{selectedItem.title}
							</Typography>
							<Typography variant="body2" color="textSecondary" component="p">
								{selectedItem.description}
							</Typography>
						</CardContent>
						<CardActions disableSpacing className="justify-content-end p-0">
							<Button
								size="small"
								variant="outlined"
								onClick={handleToggleDialogImg({})}
							>
								Close
							</Button>
						</CardActions>
					</Card>
				</DialogContent>
			</Dialog>

			{/* Toast */}
			<Snackbar
				open={showToast}
				autoHideDuration={6000}
				onClose={() => setShowToast(false)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<SnackbarContent
					style={{ background: toastBgColor }}
					message={toastMessage}
					action={
						<IconButton color="secondary" onClick={() => setShowToast(false)}>
							<Close color="action" />
						</IconButton>
					}
				/>
			</Snackbar>
		</HelpPageWrapper>
	);
}

// style
const HelpPageWrapper = styled.div`
	background-color: #f7f7f7;
	position: relative;
	padding-top: 15px;
	min-height: 50vh;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	@media (max-width: 989px) {
		padding-top: 70px;
	}
	& > div.row {
		background-color: #fff;
		padding: 30px 20px;
		border-radius: 10px;
		box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
	}
`;

const TableContent = styled.div`
	table {
		thead tr th {
			width: initial;
			vertical-align: middle;
			font-weight: 700;
			font-size: 0.95rem;
			background: #efefef;
		}

		tbody tr td {
			vertical-align: middle;
			white-space: nowrap;
			font-size: 0.95rem;

			&.modified {
				color: #ff963e;

				&.allowedPaymentMethod {
					& > * {
						color: #ff963e;
						border-color: #ff963e;
					}
				}
			}
		}
	}

	.MuiDialogContent-dividers {
		padding: 16px;
	}

	.MuiTableContainer-root {
		::-webkit-scrollbar {
			width: 5px;
			height: 5px;
			border-radius: 5px;
		}

		::-webkit-scrollbar-thumb {
			background: #888;
			border-radius: 5px;

			&:hover {
				background: #555;
			}
		}
	}
`;

const FormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

export default StoreLogo;
